import { useEffect, useState } from 'react';
import { X, ExternalLink } from 'lucide-react';

/**
 * Sticky banner shown only when the user lands on the OLD Emergent preview
 * domain (virtual-trek.emergent.host) instead of the canonical custom
 * domain (kenyachallenge.org).
 *
 * No redirect — just a one-tap nudge so walkers self-migrate. Dismissal is
 * remembered in localStorage so the banner doesn't nag forever, but the
 * "Switch to kenyachallenge.org" CTA is always one click away if they want
 * it.
 */
const OLD_HOSTS = ['virtual-trek.emergent.host'];
const CANONICAL = 'https://kenyachallenge.org';
const DISMISS_KEY = 'kc.oldDomainBannerDismissedAt';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export default function OldDomainBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const host = window.location.hostname;
    if (!OLD_HOSTS.includes(host)) return;
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;
    setShow(true);
  }, []);

  if (!show) return null;

  const targetUrl =
    CANONICAL + window.location.pathname + window.location.search + window.location.hash;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setShow(false);
  };

  return (
    <div
      className="sticky top-0 z-50 w-full bg-amber-100 border-b border-amber-300 text-amber-900"
      data-testid="old-domain-banner"
      role="alert"
    >
      <div className="container-app py-2.5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm leading-snug">
          <strong>You're on our old preview URL.</strong>{' '}
          Please switch to <code className="bg-white/60 px-1.5 py-0.5 rounded">kenyachallenge.org</code> to get the latest version.
        </p>
        <div className="flex items-center gap-2">
          <a
            href={targetUrl}
            className="inline-flex items-center gap-1.5 rounded-full bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium px-4 py-1.5 transition-colors"
            data-testid="old-domain-banner-switch"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open kenyachallenge.org
          </a>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="text-amber-700 hover:text-amber-900 p-1 rounded-full hover:bg-amber-200/60 transition-colors"
            data-testid="old-domain-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
